import * as React from "react"
import { Check, ChevronDown, Search } from "lucide-react"
import * as SelectPrimitive from "@radix-ui/react-select"

import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"

interface SearchableSelectProps {
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  items: Array<{
    value: string
    label: string
    disabled?: boolean
  }>
  disabled?: boolean
  triggerClassName?: string
  contentClassName?: string
  id?: string
}

const SearchableSelect = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SearchableSelectProps
>(
  (
    {
      value,
      onValueChange,
      placeholder = "Selecciona una opción...",
      searchPlaceholder = "Buscar...",
      items,
      disabled = false,
      triggerClassName,
      contentClassName,
      id,
    },
    ref
  ) => {
    const [searchQuery, setSearchQuery] = React.useState("")
    const [open, setOpen] = React.useState(false)

    const filteredItems = React.useMemo(() => {
      if (!searchQuery.trim()) return items
      const query = searchQuery.toLowerCase()
      return items.filter((item) => item.label.toLowerCase().includes(query))
    }, [items, searchQuery])

    const handleValueChange = (newValue: string) => {
      onValueChange(newValue)
      setSearchQuery("")
      setOpen(false)
    }

    const handleOpenChange = (isOpen: boolean) => {
      setOpen(isOpen)
      if (!isOpen) {
        setSearchQuery("")
      }
    }

    const selectedItem = items.find((item) => item.value === value)

    return (
      <SelectPrimitive.Root
        value={value}
        onValueChange={handleValueChange}
        open={open}
        onOpenChange={handleOpenChange}
        disabled={disabled}
      >
        <SelectPrimitive.Trigger
          ref={ref}
          id={id}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
            triggerClassName
          )}
        >
          <SelectPrimitive.Value placeholder={placeholder}>
            {selectedItem?.label || value}
          </SelectPrimitive.Value>
          <SelectPrimitive.Icon asChild>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            className={cn(
              "relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]",
              contentClassName
            )}
            position="popper"
          >
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      if (filteredItems.length > 0 && !filteredItems[0].disabled) {
                        handleValueChange(filteredItems[0].value)
                      }
                    }
                  }}
                />
              </div>
            </div>
            <SelectPrimitive.Viewport className="p-1 max-h-[300px] overflow-y-auto">
              {filteredItems.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  No se encontraron resultados
                </div>
              ) : (
                filteredItems.map((item) => (
                  <SelectPrimitive.Item
                    key={item.value}
                    value={item.value}
                    disabled={item.disabled}
                    className={cn(
                      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                      value === item.value && "bg-accent"
                    )}
                  >
                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                      <SelectPrimitive.ItemIndicator>
                        <Check className="h-4 w-4" />
                      </SelectPrimitive.ItemIndicator>
                    </span>
                    <SelectPrimitive.ItemText>{item.label}</SelectPrimitive.ItemText>
                  </SelectPrimitive.Item>
                ))
              )}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    )
  }
)
SearchableSelect.displayName = "SearchableSelect"

export { SearchableSelect }
